import React from "react";

import Page from "EmployeeApplication/Page";
import { stepCompleted } from "EmployerApplication/Actions";
import { useActivateEmployeeRenewalsMutation, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import {
  getCompanyPolicy,
  getEmployeeForms,
  getAnyCurrentEmployeePolicyNotActivated,
  getCarrierChanged,
  getPlanCountChanged,
} from "EmployerApplication/Selectors";
import { renewalCoverageOptionsURL } from "EmployerApplication/urls";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { formatNameLF } from "Utils/formatName";
import { validateRequired } from "Utils/Validations";

import { Table, TR, TD, TH } from "Cobalt/Table";
import AppearingElement from "Common/AppearingElement";
import BodyText from "Common/BodyText";
import { CardHeader, CardSection } from "Common/Card";
import { SIFinalFormRadioGroup } from "Common/forms";
import { Row } from "Common/Grid";
import NavigationControls from "Common/NavigationControls";

import type { EmployerRenewalEmployeeForm } from "Utils/types";

const formFormatCompanyPolicy = (companyPolicy, requireOpenEnrollment) =>
  companyPolicy
    ? {
        ...companyPolicy,
        skipOpenEnrollment: requireOpenEnrollment
          ? "false"
          : companyPolicy.skipOpenEnrollment === false || companyPolicy.skipOpenEnrollment
            ? companyPolicy.skipOpenEnrollment.toString()
            : null,
      }
    : {};

type Props = {
  employeeForms: Array<EmployerRenewalEmployeeForm>;
  anyEmployeePolicyNotActivated: boolean;
  skipOpenEnrollment: boolean | null | undefined;
  requireOpenEnrollment: boolean;
  companyPolicyId: string;
};

export const OpenEnrollmentPage = ({ employeeForms, anyEmployeePolicyNotActivated, skipOpenEnrollment, requireOpenEnrollment, companyPolicyId }: Props) => {
  const dispatch = useDispatch();
  const [activateEmployeeRenewals] = useActivateEmployeeRenewalsMutation();

  const handleSubmit = async (values) => {
    await activateEmployeeRenewals({ id: companyPolicyId, ...values }).unwrap();
    dispatch(stepCompleted("openEnrollment"));
  };

  return (
    <Page title="Employee Open Enrollment">
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          skipOpenEnrollment,
        }}
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            <CardHeader>Open Enrollment</CardHeader>
            <CardSection>
              {requireOpenEnrollment ? (
                <div>
                  <BodyText>
                    Since you have selected to make changes at renewal, each employee must log in to confirm their family members' new health insurance rates.
                    You can also make these selections on behalf of your employees.
                  </BodyText>
                  <Row className="hidden">
                    <SIFinalFormRadioGroup
                      name="skipOpenEnrollment"
                      label="Offer open enrollment?"
                      options={{
                        false: [
                          <strong key={1}>Employees will have an open enrollment (Recommended).</strong>,
                          "  Each employee must log in to confirm their family members and new health insurance rates.",
                        ],
                        true: [<strong key={2}>Employees will automatically renew.</strong>, "  Each employee will automatically be switched to the new plan."],
                      }}
                      guidance={["You can offer your employees the chance to make changes to their plan selections."]}
                      validate={validateRequired}
                    />
                  </Row>
                </div>
              ) : (
                <Row>
                  <SIFinalFormRadioGroup
                    name="skipOpenEnrollment"
                    label="Offer open enrollment?"
                    options={{
                      false: [
                        <strong key={1}>Employees will have an open enrollment (Recommended).</strong>,
                        "  Each employee must log in to confirm their family members and new health insurance rates.",
                      ],
                      true: [<strong key={2}>Employees will automatically renew.</strong>, "  Each employee will automatically be switched to the new plan."],
                    }}
                    guidance={["You can offer your employees the chance to make changes to their plan selections."]}
                    validate={validateRequired}
                  />
                </Row>
              )}

              <AppearingElement show={values.skipOpenEnrollment === false}>
                <EmployeeRenewalsTable employeeForms={employeeForms} />
              </AppearingElement>
            </CardSection>
            <NavigationControls
              backLink={renewalCoverageOptionsURL}
              saveButtonText={
                values.skipOpenEnrollment === true ? "Skip Open Enrollment" : anyEmployeePolicyNotActivated ? "Send Renewals" : "Save and Continue"
              }
            />
          </form>
        )}
      />
    </Page>
  );
};

type EmployeeRenewalsTableProps = {
  employeeForms: Array<EmployerRenewalEmployeeForm>;
};

export const EmployeeRenewalsTable = ({ employeeForms }: EmployeeRenewalsTableProps) => {
  const employeeRows = employeeForms
    .filter((employeeForm) => ["Declined", "Enrolled", "In Progress", "Not Started", "Ineligible"].includes(employeeForm.currentStatus))
    .filter((employeeForm) => !employeeForm.terminatedAt)
    .map((employee) => <EmployeeRenewalStatus key={employee.id} employee={employee} />);

  return (
    <Table>
      <thead>
        <TR>
          <TH>Name</TH>
          <TH>Email</TH>
          <TH>Current Status</TH>
        </TR>
      </thead>
      <tbody>{employeeRows}</tbody>
    </Table>
  );
};

type EmployeeRenewalStatusProps = {
  employee: EmployerRenewalEmployeeForm;
};

export const EmployeeRenewalStatus = ({ employee }: EmployeeRenewalStatusProps) => {
  const emailElement = employee.email || "No email address in system";
  let statusElement = null;

  // eslint-disable-next-line default-case
  switch (employee.currentStatus) {
    case "Declined":
      statusElement = <div>Declined </div>;
      break;
    case "Enrolled":
      statusElement = <div> Enrolled </div>;
      break;
    case "In Progress":
      statusElement = <div> In Progress </div>;
      break;
    case "Not Started":
      statusElement = <div>Not Started </div>;
      break;
    case "Ineligible":
      statusElement = <div>Ineligible</div>;
      break;
  }

  return (
    <TR highlightFirstCell={false}>
      <TD>{formatNameLF(employee)}</TD>
      <TD>{emailElement}</TD>
      <TD>{statusElement}</TD>
    </TR>
  );
};

const stringToBoolean = (string: string): boolean | null | undefined => {
  switch (string) {
    case "true":
      return true;
    case "false":
      return false;
    default:
      return null;
  }
};

const OpenEnrollmentPageWrapper = () => {
  const companyPolicy = useSelector(getCompanyPolicy);
  const employeeForms = useSelector(getEmployeeForms);
  const carrierChanged = useSelector(getCarrierChanged);
  const planCountChanged = useSelector(getPlanCountChanged);
  const forceOpenEnrollment = Boolean(companyPolicy?.forceOpenEnrollment);
  const requireOpenEnrollment = carrierChanged || planCountChanged || forceOpenEnrollment;
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const anyEmployeePolicyNotActivated = useSelector(getAnyCurrentEmployeePolicyNotActivated);

  if (!companyPolicyId) return;

  const initialValues = formFormatCompanyPolicy(companyPolicy, requireOpenEnrollment);
  const skipOpenEnrollment = stringToBoolean(initialValues.skipOpenEnrollment);

  return (
    <OpenEnrollmentPage
      employeeForms={employeeForms}
      anyEmployeePolicyNotActivated={anyEmployeePolicyNotActivated}
      skipOpenEnrollment={skipOpenEnrollment}
      requireOpenEnrollment={requireOpenEnrollment}
      companyPolicyId={companyPolicyId}
    />
  );
};

export default OpenEnrollmentPageWrapper;
